import { getPlayer } from "@Containers/game/builder/functions/player-functions";
import { fieldToScreen, getPassFlightTime, getPointAtTime, lerp, screenToField } from "@Containers/game/builder/functions/utilities";
import { BallPoint, BallState, BuilderState, ScenarioClip } from "@Containers/game/builder/models/builder-data-types";
import ScenarioActionType from "@Enums/ScenarioActionType";
import ReplayTeams from "@Shared/enums/ReplayTeams";
import { ReplayData } from "@Shared/types/ReplayData";

const DEFAULT_PASS_FLIGHT_SCALE = 500;
const DEFAULT_SHOT_FLIGHT_SCALE = DEFAULT_PASS_FLIGHT_SCALE * 1.25;

const getReplayTeam = (team: number) => (team === 1 ? ReplayTeams.TEAM_0 : ReplayTeams.TEAM_1);

const sampleTimes = (duration: number, step: number) => {
  if (duration <= 0) return [0];

  const safeStep = Math.max(0.05, step);
  const times: number[] = [];

  for (let t = 0; t < duration; t += safeStep) {
    times.push(Number(t.toFixed(4)));
  }

  if (times.length === 0 || times[times.length - 1] !== duration) {
    times.push(duration);
  }

  return times;
};

export const parseTrackedPossessorId = (possessor?: string) => {
  if (typeof possessor !== "string") return undefined;
  const parsed = Number.parseInt(possessor, 10);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const buildBallStatesFromBallPoints = (params: {
  ballPoints: BallPoint[];
  initialPossessor: number;
  initialPosition: { x: number; y: number };
}) => {
  const { ballPoints, initialPossessor, initialPosition } = params;

  if (!ballPoints.length) {
    return [];
  }

  const sortedPoints = [...ballPoints].sort((a, b) => a.t - b.t);
  const trackedStates: BallState[] = sortedPoints.map(point => ({
    state: "idle",
    time: point.t,
    position: { x: point.position.x, y: point.position.y },
    player: parseTrackedPossessorId(point.possessor),
  }));

  if (trackedStates[0]?.time === 0) {
    return trackedStates;
  }

  return [
    {
      state: "idle",
      time: 0,
      position: initialPosition,
      player: initialPossessor,
    },
    ...trackedStates,
  ];
};

export const findPossessorAtTime = (params: {
  ballStates: BallState[];
  ballPoints?: BallPoint[];
  time: number;
  initialPossessor: number;
}) => {
  const { ballStates, ballPoints = [], time, initialPossessor } = params;
  const trackedBallStates = buildBallStatesFromBallPoints({
    ballPoints,
    initialPossessor,
    initialPosition: { x: 0, y: 0 },
  });
  const stableBallStates = trackedBallStates.length > 0 ? trackedBallStates : ballStates;

  if (!stableBallStates.length) {
    return initialPossessor;
  }

  let stateIndex = stableBallStates.length - 1;
  for (let i = 0; i < stableBallStates.length - 1; i++) {
    if (stableBallStates[i].time <= time && stableBallStates[i + 1].time >= time) {
      stateIndex = i;
      break;
    }
  }

  const ballState = stableBallStates[stateIndex];

  if (ballState.state === "possessed" && typeof ballState.player === "number") return ballState.player;
  if (ballState.state === "passed" && typeof ballState.kicker === "number") return ballState.kicker;
  if (ballState.state === "kicked" && typeof ballState.kicker === "number") return ballState.kicker;
  if (ballState.state === "idle" && typeof ballState.player === "number") return ballState.player;

  return initialPossessor;
};

export const mapScreenToGameCoords = (position: { x: number; y: number }, field: BuilderState["field"]) => {
  const fieldPos = screenToField(position, field);
  return { x: fieldPos.x, y: fieldPos.y };
};

export const mapGameCoordsToScreen = (position: { x: number; y: number }, field: BuilderState["field"]) => {
  const screenPos = fieldToScreen({ x: position.x, y: position.y }, field);
  return { x: screenPos.x, y: screenPos.y };
};

const getBallScreenPositionAtTime = (time: number, ballStates: BallState[], clip: ScenarioClip) => {
  if (!ballStates.length) {
    return { ...clip.scenario.ball.initialPosition, z: 0 };
  }

  let stateIndex = ballStates.length - 1;
  for (let i = 0; i < ballStates.length - 1; i++) {
    if (ballStates[i].time <= time && ballStates[i + 1].time >= time) {
      stateIndex = i;
      break;
    }
  }

  const ballState = ballStates[stateIndex];

  if (ballState.state === "idle" && ballState.position) {
    return { ...ballState.position, z: 0 };
  }

  if (ballState.state === "possessed" && typeof ballState.player === "number") {
    const player = getPlayer(clip, ballState.player);
    if (player) {
      const playerPos = getPointAtTime(player.points, time, player.beziers);
      return { ...playerPos.position, z: 0 };
    }
  }

  if (
    ballState.state === "passed" &&
    typeof ballState.kicker === "number" &&
    typeof ballState.receiver === "number" &&
    typeof ballState.receiveTime === "number"
  ) {
    const kicker = getPlayer(clip, ballState.kicker);
    const receiver = getPlayer(clip, ballState.receiver);

    if (kicker && receiver) {
      const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers).position;
      const receiverPos = getPointAtTime(receiver.points, ballState.receiveTime, receiver.beziers).position;
      const t = (time - ballState.time) / (ballState.receiveTime - ballState.time);

      return {
        x: lerp(kickerPos.x, receiverPos.x, t),
        y: lerp(kickerPos.y, receiverPos.y, t),
        z: 0,
      };
    }
  }

  if (ballState.state === "kicked" && typeof ballState.kicker === "number" && ballState.target && typeof ballState.arriveTime === "number") {
    const kicker = getPlayer(clip, ballState.kicker);

    if (kicker) {
      const kickerPos = getPointAtTime(kicker.points, ballState.time, kicker.beziers).position;
      const t = (time - ballState.time) / (ballState.arriveTime - ballState.time);

      return {
        x: lerp(kickerPos.x, ballState.target.x, t),
        y: lerp(kickerPos.y, ballState.target.y, t),
        z: 0,
      };
    }
  }

  return { ...clip.scenario.ball.initialPosition, z: 0 };
};

const buildBallStatesFromClip = (clip: ScenarioClip): BallState[] => {
  const passChain: BallState[] = [];

  clip.scenario.players.forEach(player => {
    player.actions.forEach((action, actionIndex) => {
      if (action.type === ScenarioActionType.PassTarget) {
        const passPosition = getPointAtTime(player.points, action.startTime, player.beziers).position;
        const receiver = getPlayer(clip, action.targetPlayer);

        if (!receiver) {
          return;
        }

        const receiverPos = getPointAtTime(receiver.points, action.startTime, receiver.beziers).position;
        const receiveTime =
          Number.isFinite(action.time) && action.time >= action.startTime
            ? action.time
            : action.startTime + getPassFlightTime(passPosition, receiverPos, DEFAULT_PASS_FLIGHT_SCALE);

        passChain.push({
          state: "passed",
          time: action.startTime,
          kicker: player.id,
          receiver: action.targetPlayer,
          receiveTime,
          actionIndex,
        });
        return;
      }

      if (action.type === ScenarioActionType.ShootDirection) {
        const kickPosition = getPointAtTime(player.points, action.startTime, player.beziers).position;
        const targetPosition = action.targetPosition || { x: 0, y: 0 };

        passChain.push({
          state: "kicked",
          time: action.startTime,
          kicker: player.id,
          target: targetPosition,
          arriveTime: action.startTime + getPassFlightTime(kickPosition, targetPosition, DEFAULT_SHOT_FLIGHT_SCALE),
          actionIndex,
        });
      }
    });
  });

  passChain.sort((a, b) => a.time - b.time);

  const chain: BallState[] = [{ state: "possessed", time: 0, player: clip.scenario.ball.initialPossessor }];
  for (const state of passChain) {
    chain.push(state);
    if (state.state === "passed") {
      chain.push({ state: "possessed", time: state.receiveTime || 0, player: state.receiver });
    }
  }

  return chain;
};

const getStableBallStates = (clip: ScenarioClip, ballStates: BallState[]) => {
  const trackedBallStates = buildBallStatesFromBallPoints({
    ballPoints: clip.scenario.ball.points || [],
    initialPossessor: clip.scenario.ball.initialPossessor,
    initialPosition: clip.scenario.ball.initialPosition,
  });

  if (trackedBallStates.length > 0) {
    return trackedBallStates;
  }

  const placeholderOnly = ballStates.length === 1 && ballStates[0].state === "idle" && !ballStates[0].position;

  if (placeholderOnly || ballStates.length === 0) {
    return buildBallStatesFromClip(clip);
  }

  return ballStates;
};

export const buildReplayDataFromScenarioClip = (params: {
  clip: ScenarioClip;
  field: BuilderState["field"];
  ballStates: BallState[];
  sampleStep: number;
}): ReplayData => {
  const { clip, field, ballStates, sampleStep } = params;
  const stableBallStates = getStableBallStates(clip, ballStates);
  const duration = Math.max(0, clip.scenario.endTime || 0);
  const times = sampleTimes(duration, sampleStep);

  const characters = Array.from(clip.scenario.players.values()).map(player => {
    const position = times.map(time => {
      const pos = getPointAtTime(player.points, time, player.beziers).position;
      const gamePos = mapScreenToGameCoords(pos, field);
      return { time, x: gamePos.x, y: gamePos.y, z: 0 };
    });

    const rotation = times.map(time => {
      const currentPos = getPointAtTime(player.points, time, player.beziers).position;
      const nextPos = getPointAtTime(player.points, Math.min(duration, time + sampleStep), player.beziers).position;
      const dx = nextPos.x - currentPos.x;
      const dy = nextPos.y - currentPos.y;
      const angle = dx === 0 && dy === 0 ? 0 : (Math.atan2(dy, dx) * 180) / Math.PI;
      return { time, angle };
    });

    return {
      id: player.id.toString(),
      isPlayer: player.isPlayer,
      jerseyNumber: player.jerseyNumber,
      name: player.name,
      position,
      rotation,
      team: getReplayTeam(player.team),
    };
  });

  const ballPosition = times.map(time => {
    const pos = getBallScreenPositionAtTime(time, stableBallStates, clip);
    const gamePos = mapScreenToGameCoords(pos, field);
    return { time, x: gamePos.x, y: gamePos.y, z: pos.z ?? 0 };
  });

  return {
    duration,
    characters,
    ball: {
      position: ballPosition,
      rotation: times.map(time => ({ time, angle: 0 })),
    },
  };
};
